import React from 'react';

/**
 * SplitSlider — reusable percentage split component.
 * ─────────────────────────────────────────────────────────────────
 * Identical interaction pattern to SplitField in DecisionGuru:
 *   • Draggable stacked colour bar (drag dividers between segments)
 *   • Per-segment number inputs
 *   • Option chip pills to toggle segments on/off
 *   • Live total badge (green at 100%, red otherwise)
 *
 * Props:
 *   label      string        Section label shown above the bar
 *   options    Array<{ key: string, label: string, colour: string }>
 *   value      { active: string[], splits: { [key]: number } }
 *   onChange         (newValue) => void
 *   editableKeys     Array<string> Optional. Option keys whose label
 *                    the user can rename — typically 'other' so the
 *                    leader can spell out what "Other" means. When an
 *                    editable option is active, a small text input
 *                    appears next to its row to set value.labels[key].
 *
 * Value shape:
 *   { active: ['delivery','sales'],
 *     splits: { delivery: 60, sales: 40 },
 *     labels?: { other: 'Mentoring' } }
 *
 * Initial value when none provided:
 *   First option at 100%.
 *
 * Usage:
 *   import SplitSlider from '../components/SplitSlider';
 *
 *   const OPTIONS = [
 *     { key: 'delivery', label: 'Delivery / client work', colour: '#1D9E75' },
 *     { key: 'sales',    label: 'Sales & business dev',   colour: '#185FA5' },
 *     ...
 *   ];
 *
 *   <SplitSlider
 *     label="Founder time allocation"
 *     options={OPTIONS}
 *     value={founderTime}
 *     onChange={setFounderTime}
 *   />
 */

function normaliseSplitState(splitState) {
  if (!splitState || !splitState.active || !splitState.active.length) return splitState;
  const total = splitState.active.reduce((s, k) => s + (splitState.splits[k] || 0), 0);
  if (total === 0 || total === 100) return splitState;
  const newSplits = { ...splitState.splits };
  let sum = 0;
  splitState.active.forEach((k, i) => {
    if (i === splitState.active.length - 1) {
      newSplits[k] = 100 - sum;
    } else {
      newSplits[k] = Math.round((splitState.splits[k] || 0) * 100 / total);
      sum += newSplits[k];
    }
  });
  return { ...splitState, splits: newSplits };
}

export default function SplitSlider({ label, options, value, onChange, editableKeys }) {
  const barRef = React.useRef(null);
  const editableSet = React.useMemo(() => new Set(editableKeys || []), [editableKeys]);

  // Default: first option at 100%
  const st = (value && value.active && value.active.length > 0)
    ? value
    : { active: [options[0].key], splits: { [options[0].key]: 100 } };

  const getColour = (key) => options.find(o => o.key === key)?.colour || '#888';
  // Custom label override (from value.labels) wins when the option's
  // key is in editableKeys and a non-empty label has been set.
  const getLabel = (key) => {
    const override = editableSet.has(key) ? st.labels?.[key] : null;
    if (override && override.trim()) return override.trim();
    return options.find(o => o.key === key)?.label || key;
  };

  function setCustomLabel(key, text) {
    const nextLabels = { ...(st.labels || {}) };
    if (text && text.trim()) nextLabels[key] = text;
    else delete nextLabels[key];
    onChange({ ...st, labels: nextLabels });
  }
  const total = st.active.reduce((s, k) => s + (st.splits[k] || 0), 0);

  function updateSplit(key, val) {
    const clamped = Math.max(1, Math.min(100, Math.round(Number(val) || 1)));
    onChange({ ...st, splits: { ...st.splits, [key]: clamped } });
  }

  function toggleOption(key) {
    if (st.active.includes(key)) {
      // Remove — must keep at least one
      if (st.active.length === 1) return;
      const removed = st.splits[key] || 0;
      const newActive = st.active.filter(k => k !== key);
      const newSplits = { ...st.splits };
      delete newSplits[key];
      // Give removed % to the last remaining segment
      const last = newActive[newActive.length - 1];
      newSplits[last] = (newSplits[last] || 0) + removed;
      onChange(normaliseSplitState({ active: newActive, splits: newSplits }));
    } else {
      // Add — give it 20% and normalise the rest
      const newActive = [...st.active, key];
      const newSplits = { ...st.splits, [key]: 20 };
      onChange(normaliseSplitState({ active: newActive, splits: newSplits }));
    }
  }

  function startDrag(e, leftIdx) {
    if (!barRef.current) return;
    e.preventDefault();
    const startX = e.clientX ?? e.touches?.[0]?.clientX;
    const barW = barRef.current.getBoundingClientRect().width;
    const lk = st.active[leftIdx];
    const rk = st.active[leftIdx + 1];
    const sl = st.splits[lk] || 0;
    const sr = st.splits[rk] || 0;

    const move = (cx) => {
      const dp = ((cx - startX) / barW) * 100;
      const newL = Math.round(Math.min(Math.max(sl + dp, 5), sl + sr - 5));
      onChange({ ...st, splits: { ...st.splits, [lk]: newL, [rk]: sl + sr - newL } });
    };
    const mm = ev => move(ev.clientX);
    const tm = ev => move(ev.touches[0].clientX);
    const end = () => {
      document.removeEventListener('mousemove', mm);
      document.removeEventListener('mouseup', end);
      document.removeEventListener('touchmove', tm);
      document.removeEventListener('touchend', end);
    };
    document.addEventListener('mousemove', mm);
    document.addEventListener('mouseup', end);
    document.addEventListener('touchmove', tm);
    document.addEventListener('touchend', end);
  }

  return (
    <div>
      {/* Label row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.5rem' }}>
        {label && <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1a1a2e' }}>{label}</div>}
        <span style={{
          fontSize: '0.72rem', fontWeight: 600, padding: '2px 8px', borderRadius: 20,
          background: total === 100 ? '#E1F5EE' : '#FCEBEB',
          color: total === 100 ? '#0F6E56' : '#A32D2D',
          marginLeft: 'auto',
        }}>
          {total}%
        </span>
      </div>

      {/* Draggable stacked bar */}
      <div ref={barRef} style={{
        display: 'flex', height: 36, borderRadius: 8, overflow: 'hidden',
        userSelect: 'none', cursor: 'default', marginBottom: '0.6rem',
      }}>
        {st.active.map((key, idx) => {
          const pct = st.splits[key] || 0;
          const colour = getColour(key);
          return (
            <div key={key} style={{
              width: `${pct}%`, background: colour,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              position: 'relative', overflow: 'hidden', minWidth: 0, transition: 'width 0.1s',
            }}>
              {pct > 12 && (
                <span style={{ fontSize: 10, fontWeight: 600, color: 'white', padding: '0 6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {getLabel(key)}
                </span>
              )}
              {/* Drag handle between segments */}
              {idx < st.active.length - 1 && (
                <div
                  onMouseDown={e => startDrag(e, idx)}
                  onTouchStart={e => startDrag(e, idx)}
                  style={{
                    position: 'absolute', top: 0, bottom: 0, right: -5, width: 10,
                    cursor: 'col-resize', zIndex: 10,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(255,255,255,0.25)',
                  }}>
                  <div style={{ width: 2, height: 14, background: 'rgba(255,255,255,0.8)', borderRadius: 1 }} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Per-segment number inputs */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '0.6rem' }}>
        {st.active.map((key) => {
          const editable = editableSet.has(key);
          const defaultLabel = options.find(o => o.key === key)?.label || key;
          return (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{ width: 9, height: 9, borderRadius: '50%', background: getColour(key), flexShrink: 0 }} />
              {editable ? (
                <input
                  type="text"
                  value={st.labels?.[key] || ''}
                  placeholder={`${defaultLabel} — name it`}
                  onChange={e => setCustomLabel(key, e.target.value)}
                  style={{ flex: 1, fontSize: '0.75rem', color: '#1a1a2e', padding: '3px 6px', border: '0.5px solid #ddd', borderRadius: 5, fontFamily: 'inherit', boxSizing: 'border-box', minWidth: 0 }}
                />
              ) : (
                <span style={{ fontSize: '0.75rem', color: '#1a1a2e', flex: 1 }}>{getLabel(key)}</span>
              )}
              <input
                type="number" min="1" max="100" step="1"
                value={Math.round(st.splits[key] || 0)}
                onChange={e => updateSplit(key, e.target.value)}
                style={{ width: 48, fontSize: '0.75rem', fontWeight: 600, padding: '3px 5px', border: '0.5px solid #ddd', borderRadius: 5, textAlign: 'center', fontFamily: 'inherit', boxSizing: 'border-box' }}
              />
              <span style={{ fontSize: '0.7rem', color: '#aaa', width: 14, flexShrink: 0 }}>%</span>
            </div>
          );
        })}
      </div>

      {/* Option chips to toggle segments */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', alignItems: 'center' }}>
        {options.map((opt) => {
          const isActive = st.active.includes(opt.key);
          const colour = isActive ? opt.colour : '#ccc';
          const activeIdx = st.active.indexOf(opt.key);
          return (
            <div
              key={opt.key}
              onClick={() => toggleOption(opt.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '3px 10px 3px 7px', borderRadius: 20, cursor: 'pointer',
                border: `0.5px solid ${isActive ? 'transparent' : '#ddd'}`,
                fontSize: '0.72rem', fontWeight: isActive ? 600 : 400,
                background: isActive ? colour : 'white',
                color: isActive ? 'white' : '#888',
                transition: 'all 0.12s',
                userSelect: 'none',
              }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: isActive ? 'rgba(255,255,255,0.55)' : '#ddd', flexShrink: 0 }} />
              {opt.label}{isActive && activeIdx >= 0 ? ` ${Math.round(st.splits[opt.key] || 0)}%` : ''}
            </div>
          );
        })}
      </div>
    </div>
  );
}
